
import InformationHeader from '../components/test-information/InformationBanner.vue';
import ItemContainer from '../components/ItemContainer.vue';
import NavigationFooter from '../components/NavigationFooter.vue';
import PlayerHeader from '../components/PlayerHeader.vue';
import { inject } from 'vue';
import NavigationDirectionEnum from '../controllers/navigation/NavigationDirectionEnum';
import PnpToolContainer from '../components/PnpToolContainer.vue';
import AlertUtility from '../util/AlertUtility';
import EventBusConstants from '../constants/EventBusConstants';
import TestController from '../controllers/TestController';
import Item from '../models/Item';
import { Options, Vue, Watch } from 'vue-property-decorator';
import PnpExpandingToolbar from '../components/PnpExpandingToolbar.vue';
import PlayerEmitter from '../event/PlayerEmitter';
import axios from 'axios';
import '../assets/css/bootstrap-material-design.min.css';
import { log, LoggingType } from '@/logging/CustomLogging';
import DisplayItem from '@/models/DisplayItem';
import TestReview from '@/components/review/TestReview.vue';
import ReviewScreen from '@/components/review/ReviewScreen.vue';
import DeliveryOption from '@/controllers/options/DeliveryOption';
import NavigationContext from '@/controllers/navigation/state/NavigationContext';
import NavigationStateDisplayType from '@/controllers/navigation/state/NavigationStateDisplayType';
import NavigationCallbackType from '@/controllers/navigation/state/NavigationCallbackType';
import DeliveryOptionConstants from '@/controllers/options/DeliveryOptionConstants';
import DeliveryOptionUtility from '@/controllers/options/DeliveryOptionUtility';
import ItemToolbar from '@/components/toolbar/ItemToolbar.vue';
import DisplayManager from '@/controllers/display/DisplayManager';
import ToolManager from '@/controllers/tools/ToolManager';
import FocusModeNavigationControl from '@/components/navigation/FocusModeNavigationControl.vue';
import TestStartController from '@/components/test-start/TestStartController.vue';
import IPersistenceStatusHandler from '@/api/IPersistenceStatusHandler';
import PersistenceStatus from '@/api/PersistenceStatus';
import PersistenceNotification from '@/components/persistence/PersistenceNotification.vue';
import PauseView from '@/components/pause/PauseView.vue';
import IPauseManagerNotificationObserver from '@/controllers/pause/IPauseManagerNotificationObserver';
import { PauseType, PauseState, PauseStateValue } from '@/controllers/pause/PauseManager';
import IPersistenceEventHandler from '@/controllers/persistence/IPersistenceEventHandler';
import PersistenceEventType from '@/controllers/persistence/PersistenceEventType';
import ApplicationErrorNotification from '@/components/persistence/ApplicationErrorNotification.vue';
import ItemStatus from '@/status/ItemStatus';
import ItemStatusCalculator from '@/status/ItemStatusCalculator';
import Test from '@/models/Test';

@Options({
  components: {
    PnpExpandingToolbar,
    PlayerHeader,
    NavigationFooter,
    InformationHeader,
    ItemContainer,
    PnpToolContainer,
    TestReview,
    ReviewScreen,
    ItemToolbar,
    FocusModeNavigationControl,
    TestStartController,
    PersistenceNotification,
    ApplicationErrorNotification,
    PauseView,
  },
})
export default class AdamQTIPlayer
  extends Vue
  implements IPersistenceStatusHandler, IPauseManagerNotificationObserver, IPersistenceEventHandler
{
  private testController: TestController | undefined = inject<TestController>('testController');
  private isLoading = false;
  private currentItem: DisplayItem | null = null;
  private pauseTest_ = false;
  private pauseSource = false;
  private lockSection = false;
  private playerEmitter: PlayerEmitter | undefined = inject<PlayerEmitter>('playerEmitter');
  private endOfTestReview = false;
  private navigationContext: NavigationContext | undefined;
  private showReviewScreen_ = false;
  private displayManager: DisplayManager = new DisplayManager();
  private toolManager: ToolManager | undefined = inject<ToolManager>('toolManager');
  private focusMode_ = false;
  private isPreviewMode_ = false;
  private loadingTest_ = true;
  private jwt_: string | undefined;
  private networkError_: PersistenceStatus = PersistenceStatus.NO_ERROR;
  private persistenceNetworkStatus_: PersistenceStatus = PersistenceStatus.NO_ERROR;
  private applicationError_ = false;
  private displayType_: NavigationStateDisplayType = NavigationStateDisplayType.ITEM_CONTAINER;
  private showContainer_ = false;
  private displayTesterPaused_ = false;
  private displayProctorPaused_ = false;
  private displaySectionPaused_ = false;
  private warnOnChange_ = false;

  private get titleDeliveryOption(): DeliveryOption | undefined {
    if (this.currentItem) {
      return DeliveryOptionUtility.getDeliveryOption(
        this.currentItem.section.testPart.deliveryOptions,
        DeliveryOptionConstants.SHOW_FORM_TITLE
      );
    }
    return undefined;
  }

  private get pauseTest(): boolean {
    return this.pauseTest_;
  }

  private get displayType(): NavigationStateDisplayType {
    this.calculateDisplayType();
    return this.displayType_;
  }

  private get waitForProctorReleaseDisplayType(): NavigationStateDisplayType {
    return NavigationStateDisplayType.WAIT_FOR_PROCTOR_RELEASE;
  }

  private showReviewProgressButton(): boolean {
    if (this.testController) {
      return this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.REVIEW_SCREEN);
    } else {
      return true;
    }
  }

  get displayTesterPaused(): boolean {
    return this.displayTesterPaused_;
  }

  get displayProctorPaused(): boolean {
    return this.displayProctorPaused_;
  }

  get displaySectionPaused(): boolean {
    return this.displaySectionPaused_;
  }

  private get loadingTest(): boolean {
    return this.loadingTest_;
  }

  private get networkError(): PersistenceStatus {
    return this.networkError_;
  }

  private get persistenceNetworkStatus(): PersistenceStatus {
    return this.persistenceNetworkStatus_;
  }

  private get persistenceStatusNoError(): number {
    return PersistenceStatus.NO_ERROR;
  }

  private get persistenceInitialError(): number {
    return PersistenceStatus.INITIAL_ERROR;
  }

  private get applicationError(): boolean {
    return this.applicationError_;
  }

  private get persistenceSaving(): number {
    return PersistenceStatus.SAVING;
  }

  private get persistenceUnrecoverableError(): number {
    return PersistenceStatus.UNRECOVERABLE_ERROR;
  }

  private get persistenceUnrecoverableNetworkError(): number {
    return PersistenceStatus.UNRECOVERABLE_NETWORK_ERROR;
  }

  private handleTestReady(): void {
    this.setLockSection();

    this.navigationContext = new NavigationContext(
      this.testController!.navigationController!,
      this.lockSection,
      this.testController!.deliveryOptionEnabledForTest(DeliveryOptionConstants.FINAL_REVIEW_SCREEN)
    );
    this.testController!.navigationController!.navigationContext = this.navigationContext;
    this.testController!.navigationController!.navigationContext!.setNavigationStateFromLocationState(
      this.handleNavigationCallback
    );

    this.testController!.navigationController!.setSectionChangeCallback(() => {
      this.playerEmitter?.emit(EventBusConstants.SECTION_CHANGE);
    });

    this.testController!.persistenceManager?.addPersistenceEventHandler(this);

    this.warnOnChange_ = this.warnOnChange();
    this.loadingTest_ = false;
  }

  private get isPreviewMode(): boolean {
    return this.testController ? this.testController.previewMode : false;
  }

  private get displayNameDeliveryOption(): DeliveryOption | undefined {
    return this.currentItem
      ? DeliveryOptionUtility.getDeliveryOption(
          this.currentItem.section.testPart.deliveryOptions,
          DeliveryOptionConstants.STUDENT_NAME
        )
      : undefined;
  }

  private get previewModeText(): string {
    return this.$t("status.form_preview");
  }

  private get showContainer(): boolean {
    this.calculateShowContainer();
    return this.showContainer_;
  }

  private get showReviewScreen(): boolean {
    this.calculateShowReviewScreen();
    return this.showReviewScreen_;
  }

  private showItemProgress(): boolean {
    return this.testController
      ? this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.ITEM_PROGRESS)
      : true;
  }

  private showQuestionNumber(): boolean {
    return this.testController
      ? this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.QUESTION_NUMBER)
      : true;
  }

  private get pauseButtonDeliveryOption(): DeliveryOption | undefined {
    return this.currentItem
      ? DeliveryOptionUtility.getDeliveryOption(
          this.currentItem.section.testPart.deliveryOptions,
          DeliveryOptionConstants.PAUSE_BUTTON
        )
      : undefined;
  }
  private get exitButtonDeliveryOption(): DeliveryOption | undefined {
    return this.currentItem
      ? DeliveryOptionUtility.getDeliveryOption(
          this.currentItem.section.testPart.deliveryOptions,
          DeliveryOptionConstants.EXIT_BUTTON
        )
      : undefined;
  }
  private get progressBarDeliveryOption(): DeliveryOption | undefined {
    return this.currentItem
      ? DeliveryOptionUtility.getDeliveryOption(
          this.currentItem.section.testPart.deliveryOptions,
          DeliveryOptionConstants.SHOW_PROGRESS_BAR
        )
      : undefined;
  }

  private get timerDeliveryOption(): DeliveryOption | undefined {

    return this.currentItem && this.testController?.test?.locationState != Test.SECTION_INSTRUCTIONS_STATE
      ? DeliveryOptionUtility.getDeliveryOption(
          this.currentItem.section.deliveryOptions,
          DeliveryOptionConstants.TIMER_OPTION
        )
      : undefined;
  }

  private get focusModeEnabled(): boolean {
    if (this.currentItem) {
      const deliveryOption: DeliveryOption | undefined = DeliveryOptionUtility.getDeliveryOption(
        this.currentItem.section.testPart.deliveryOptions,
        DeliveryOptionConstants.FOCUS_MODE
      );
      return DeliveryOptionUtility.deliveryOptionHasExpectedValue(
        deliveryOption,
        DeliveryOptionConstants.FOCUS_MODE,
        'value',
        'true'
      );
    }
    return false;
  }

  //IPersistenceEventHandler
  persistenceEvent(type: PersistenceEventType) {
    const persistenceNotificationContainer = this.$el.querySelector('#persistence-notification-container');

    if (persistenceNotificationContainer) {
      persistenceNotificationContainer.classList.remove('overlay-transparent');
      if (type === PersistenceEventType.SAVING) {
        persistenceNotificationContainer.classList.add('overlay-transparent');
      }
    }

    switch (type) {
      case PersistenceEventType.SAVING: {
        this.persistenceNetworkStatus_ = this.persistenceSaving;
        break;
      }
      case PersistenceEventType.COMPLETE: {
        this.persistenceNetworkStatus_ = this.persistenceStatusNoError;
        break;
      }
      case PersistenceEventType.FINAL: {
        this.persistenceNetworkStatus_ = this.persistenceStatusNoError;
        this.testController?.reset();
        this.handleRedirect(this.$t('review.end'), this.$t('review.close_window'), 0);
        break;
      }
      case PersistenceEventType.ERROR: {
        this.$cookies.remove('qti-player-token');
        this.testController?.reset();
        this.persistenceNetworkStatus_ = this.persistenceUnrecoverableError;
        break;
      }
    }
  }

  // IPersistenceStatusHandler
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handlePersistenceError(value: PersistenceStatus): void {
    // Not used
  }

  //IPauseStateNotificationObserver
  pauseStateNotification(type: PauseType, state: PauseState) {
    const show: boolean = state === PauseState.PAUSE ? true : false;

    if (state === PauseState.PAUSE) {
      this.pauseTest_ = true;
    } else if (state === PauseState.UNPAUSE) {
      this.pauseTest_ = false;
    }

    if (type === PauseType.PROCTOR_PAUSE) {
      this.displayProctorPaused_ = show;
      if (show) {
        this.displayTesterPaused_ = false;
      }
    } else if (type === PauseType.SECTION_PAUSE) {
      if (this.displaySectionPaused_ && !show) {
        this.testController?.releaseSection();
      }
      this.displaySectionPaused_ = show;
    } else if (type === PauseType.TESTER_PAUSE) {
      this.displayTesterPaused_ = show;
    } else if (type === PauseType.RESEAT || type === PauseType.SUBMITTED) {
      this.handleExitTest();
    }
  }

  //IPauseStateNotificationObserver
  pauseError(error: Error) {
    log(error.message, LoggingType.ERROR);
    this.handleNetworkError();
  }

  private handleNetworkError(): void {
    this.$cookies.remove('qti-player-token');
    this.networkError_ = PersistenceStatus.UNRECOVERABLE_NETWORK_ERROR;
  }

  private focusModeClicked(): void {
    this.focusMode_ = !this.focusMode_;
    const focusModeButton = this.$el.querySelector('#focus-mode-button');
    const focusModeButtonIcon = this.$el.querySelector('#focus-mode-button-icon');

    focusModeButton.setAttribute('title', this.focusMode ? this.$t('toolbar.exit_focus_mode') : this.$t('toolbar.enter_focus_mode'));
    focusModeButtonIcon.classList.remove('fa-expand', 'fa-compress');
    focusModeButtonIcon.classList.add(this.focusMode_ ? 'fa-compress' : 'fa-expand');

    const verticalToolbarContainer: Element = this.$el.querySelector('#vertical-toolbar-container');
    if (verticalToolbarContainer) {
      verticalToolbarContainer.classList.remove('regular-toolbar-position');
      verticalToolbarContainer.classList.remove('focus-mode-toolbar-position');

      if (this.focusMode) {
        verticalToolbarContainer.classList.add('focus-mode-toolbar-position');
      } else {
        verticalToolbarContainer.classList.add('regular-toolbar-position');
      }
    }
  }

  private get focusMode(): boolean {
    return this.focusMode_;
  }

  private getItemDeliveryOptions(): Array<DeliveryOption> {
    let array: Array<DeliveryOption> = new Array<DeliveryOption>();

    if (this.currentItem) {
      if (this.currentItem instanceof Item) {
        array = array.concat(this.currentItem.deliveryOptions);
      }

      const textToSpeechOption: DeliveryOption | undefined = DeliveryOptionUtility.getDeliveryOption(
        this.currentItem.section.testPart.deliveryOptions,
        DeliveryOptionConstants.TEXT_TO_SPEECH
      );
      if (textToSpeechOption) {
        array.push(textToSpeechOption);
      }

      const focusMode: DeliveryOption | undefined = DeliveryOptionUtility.getDeliveryOption(
        this.currentItem.section.testPart.deliveryOptions,
        DeliveryOptionConstants.FOCUS_MODE
      );
      if (focusMode) {
        array.push(focusMode);
      }
    }
    return array;
  }

  private sectionLockEnabled(): boolean {
    if (this.currentItem) {
      return DeliveryOptionUtility.getDeliveryOptionAndCheckExpectedValue(
        this.currentItem.section.testPart.deliveryOptions,
        DeliveryOptionConstants.BLOCK_RETURN_TO_SECTION,
        'value',
        'true'
      );
    } else {
      return false;
    }
  }

  private sectionLogoutEnabled(): boolean {
    if (this.currentItem) {
      return DeliveryOptionUtility.getDeliveryOptionAndCheckExpectedValue(
        this.currentItem.section.testPart.deliveryOptions,
        DeliveryOptionConstants.LOGOUT_SECTION,
        'value',
        'true'
      );
    } else {
      return false;
    }
  }

  private flagItemEnabled(): boolean {
    return this.testController
      ? this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.FLAG_ITEM)
      : false;
  }

  private calculateShowReviewScreen(): void {
    this.showReviewScreen_ =
      this.navigationContext !== undefined &&
      this.navigationContext.navigationState !== undefined &&
      this.navigationContext.navigationState.getDisplayType() === NavigationStateDisplayType.TEST_REVIEW;
  }

  private calculateShowContainer(): void {
    this.showContainer_ =
      this.testController !== undefined &&
      this.testController.test !== null &&
      this.currentItem !== null &&
      this.currentItem !== undefined;
  }

  private calculateDisplayType(): void {
    if (this.navigationContext && this.navigationContext.navigationState) {
      this.displayType_ = this.navigationContext.navigationState.getDisplayType();
    } else {
      this.displayType_ = NavigationStateDisplayType.ITEM_CONTAINER;
    }
  }

  private showItemToolbar(): boolean {
    const value = this.displayManager.showItemToolbar(this.currentItem);

    this.displayManager.itemToolbarVisible(value, this.$refs.itemContainer as ItemContainer);
    return value;
  }

  private getTestController(): TestController | undefined {
    return this.testController;
  }

  data() {
    return {
      isLoading: true as boolean,
      currentItem: undefined as unknown as DisplayItem,
      pauseSource: '' as string,
      navigationContext: undefined as unknown as NavigationContext,
    };
  }

  @Watch('currentItem')
  private onPropertyChanged(newValue: Item): void {
    this.toolManager!.setPnpItemArray(
      newValue
        ? DeliveryOptionUtility.combineDeliveryOptions(
            newValue.section.testPart.deliveryOptions,
            newValue.section.deliveryOptions
          )
        : []
    );
    this.setLockSection();
    if (newValue) {
      this.toolManager!.hideToolsThatShouldNotBeOpen(
        DeliveryOptionUtility.combineDeliveryOptions(
          DeliveryOptionUtility.combineDeliveryOptions(
            newValue.deliveryOptions,
            newValue.section.testPart.deliveryOptions
          ),
          newValue.section.deliveryOptions
        )
      );
    }
  }

  private setLockSection(): void {
    this.lockSection = this.testController
      ? this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.BLOCK_RETURN_TO_SECTION)
      : false;
  }

  private handleNavigationEvent(data: Map<string, unknown>): void {
    const itemContainer: ItemContainer = this.$refs.itemContainer as ItemContainer;
    if (itemContainer) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const that = this;
      itemContainer.saveItem(this.navigationContext?.isReviewScreen(), () => {
        if (!that.itemIsComplete() && !that.navigationContext?.isReviewScreen()) {
          AlertUtility.displayQuestionDialogWithCustomButtonText(
            this.$t('modal.answers_required'),
            this.$t('modal.additional_answers_info'),
            this.$t('btn.cancel'),
            this.$t('btn.continue')
          )
            .then(() => {
              that.getNextDisplayItem(data.get('direction') as NavigationDirectionEnum);
            })
            .catch(() => {
              //Do nothing
            });
        } else {
          that.getNextDisplayItem(data.get('direction') as NavigationDirectionEnum);
        }
      });
    }
  }

  private itemIsComplete(): boolean {
    if (this.warnOnChange_ && this.testController && this.testController.navigationController) {
      const item: DisplayItem | null = this.testController.navigationController.getCurrentItem();
      if (item && item instanceof Item) {
        return ItemStatusCalculator.determineItemStatus(item) === ItemStatus.FULLY_ATTEMPTED;
      }
    }
    return true;
  }

  private warnOnChange(): boolean {
    return this.testController
      ? this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.WARN_ON_CHANGE)
      : false;
  }

  mounted() {
    const jwt = this.$cookies.get('qti-player-token');
    if (!jwt) {
      log('Error starting test. Possibly trying to reload a completed test', LoggingType.ERROR);
      AlertUtility.displayErrorDialog(this.$t('common.error'), this.$t('modal.error')).finally(
        () => {
          this.redirectToPlatform();
        }
      );
      return;
    }
    this.testController!.pauseManagerNotificationObserver = this;

    axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwt;

    (this.$refs.testStartController as TestStartController).startLoading(jwt);

    this.playerEmitter!.on(EventBusConstants.NAVIGATION_EVENT, (data: Map<string, unknown>) =>
      this.handleNavigationEvent(data)
    );

    this.playerEmitter!.on(EventBusConstants.HARD_TIMER_EVENT, () => this.handleHardTimerEvent());

    this.playerEmitter!.on(EventBusConstants.ITEM_PICKED, (data) => {
      this.testController!.navigationController?.setCurrentItem(data.get('item') as DisplayItem);
      this.getNextDisplayItem(NavigationDirectionEnum.Current);
    });

    this.playerEmitter!.on(EventBusConstants.REVIEW_SUBMIT, () => {
      this.navigationContext!.navigationState?.handleReviewSubmit((callbackType: NavigationCallbackType) => {
        if (this.lockSection) {
          this.playerEmitter?.emit(EventBusConstants.REVIEW_SUBMIT_BUTTON_CLICKED);
        }
        this.handleNavigationCallback(callbackType);
      });
    });

    this.playerEmitter!.on(EventBusConstants.NETWORK_ERROR, () => {
      this.handleNetworkError();
    });

    this.playerEmitter!.on(EventBusConstants.APPLICATION_ERROR, (data) => {
      this.applicationError_ = true;
      let message: string | undefined =
        data && data.get('message') ? (data.get('message') as string) : 'Application Error';
      log(message, LoggingType.ERROR);
    });
  }

  private handleHardTimerEvent(): void {

    let isLastSection = this.testController?.navigationController?.lastSectionInTest()
    let modalMessage =  this.$t("modal.section_timer_expired")
    let modalButton = this.$t("review.continue");
    
    if (this.sectionLogoutEnabled()) {
      modalMessage =  this.$t("modal.section_logout_timer_expired")
    }

    if (isLastSection) {
      modalMessage = this.$t("modal.test_timer_expired")
      modalButton = this.$t("btn.submit_test_logout")
    }

    let currentItem = this.testController!.navigationController!.getCurrentItem()
    this.testController!.persistenceManager?.save(currentItem, true, true);
    this.testController!.persistenceManager?.save(null, true)

    AlertUtility.displayTimerDialog(modalMessage, modalButton).then(() => {
      if (isLastSection) {
        this.handleEndOfTest(this.$t('review.end'), this.$t('review.close_window'));
      } else {
        this.playerEmitter!.on(EventBusConstants.HARD_TIMER_SECTION_LOGOUT, () => {
          if (this.sectionLogoutEnabled()) {
            this.testController!.sessionController?.updatePauseState(PauseStateValue.SECTION_PAUSE).then(() => {
              if (this.testController!.getRedirectUrl != "" && !this.testController!.navigationController?.endOfTest()) {
                this.$cookies.remove('qti-player-token');
                this.testController!.reset();
                if (this.testController!.isTestNav) {
                  window.location.href = this.testController!.baseServiceUrl + '/tester?from_app="testnav"';
                } else {
                  window.location.href = this.testController!.baseServiceUrl + '/tester';
                }
              } else {
                this.handleExitTest();
              }
            });
          }
        });
        
        let currentSectionIndex = 0
        this.testController?.test?.testParts.forEach((testPart) => {
          for(let i=0; i< testPart.sections.length; i++) {
            if (currentItem!.section == testPart.sections[i]) {
              currentSectionIndex = i
            }
          }
        });

        let item = this.testController?.test?.testParts[0].sections[currentSectionIndex+1].items[0]
        this.playerEmitter!.emit(EventBusConstants.ITEM_PICKED, [
          ['item', item]
        ]);
      }
    });
  }

  private handleOutsideReviewClick(event: Event): void {
    const informationBanner = this.$refs.informationBanner as InformationHeader;
    const eventTarget = event.target as HTMLDivElement;
    if (!informationBanner.$el.contains(eventTarget) && informationBanner.expanded) {
      if (!(this.$refs.testReview as TestReview).$el.contains(eventTarget)) {
        informationBanner.closeReviewIfExpanded(event.target);
      }
    }
  }

  private handleItemContainerHeightChange(value: number): void {
    this.displayManager.itemContainerHeightChange(
      value,
      this.$refs.itemContainer as ItemContainer,
      this.$refs.navigationFooter as NavigationFooter
    );
  }

  private getNextDisplayItem(type: NavigationDirectionEnum): void {
    if (this.testController?.isProcessing) {
      return;
    }

    this.isLoading = true;
    if (type === NavigationDirectionEnum.Back) {
      const prevItem = this.testController!.navigationController?.peekPreviousItem();
      if (this.testController!.navigationController?.navigationContext?.isReviewScreen() || AlertUtility.isNavigationAllowed(prevItem)) this.navigationContext!.navigationState!.handleBackNavigation(this.handleNavigationCallback);
    } else if (type === NavigationDirectionEnum.Forward) {
      const nextItem = this.testController!.navigationController?.peekNextItem();
      if (AlertUtility.isNavigationAllowed(nextItem)) this.navigationContext!.navigationState!.handleForwardNavigation(this.handleNavigationCallback);
    } else {
      this.navigationContext!.navigationState!.handleCurrentItemNavigation(this.handleNavigationCallback);
    }
    this.isLoading = false;
  }

  private handleNavigationCallback(callbackType: NavigationCallbackType): void {
    this.calculateShowReviewScreen();
    this.calculateDisplayType();

    switch (callbackType) {
      case NavigationCallbackType.NEXT: {
        this.handleForwardNavigation();
        break;
      }
      case NavigationCallbackType.BACK: {
        this.handleBackNavigation();
        break;
      }
      case NavigationCallbackType.CURRENT: {
        this.handleCurrentItemNavigation();
        break;
      }
      case NavigationCallbackType.NONE: {
        this.testController!.persistenceManager?.save(this.currentItem, true);
        break;
      }
      default: {
        break;
      }
    }

    if (this.testController && this.testController.pauseManager) {
      //this.testController.isProcessing = true;
    }

    this.testController?.navigationEvent();
    this.calculateShowContainer();

    this.playerEmitter?.emit(EventBusConstants.HARD_TIMER_SECTION_LOGOUT);
  }

  private handleExitTest(): void {
    this.$cookies.remove('qti-player-token');
    this.testController!.reset();
    console.log('this.testController!.isTestNav ', this.testController!.isTestNav)
    if (this.testController!.isTestNav) {
      window.location.href = this.testController!.baseServiceUrl + '/tester?from_app="testnav"';
    } else {
      this.redirectToPlatform();
    }
  }

  private handleBackNavigation(): void {
    this.currentItem = this.testController!.navigationController!.getPreviousItem();
    // this.testController!.persistenceManager!.save(this.currentItem);
  }

  private handleForwardNavigation(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    this.testController!.navigationController!.getNextItem().then((item) => {
      that.currentItem = item;
      if (!that.currentItem) {
        that.handleEndOfTest(this.$t('review.end'), this.$t('review.close_window'));
      }
      if (
        that.testController!.navigationController?.beginningOfSection() &&
        !that.testController!.navigationController.isBeginningOfTest() &&
        that.sectionLogoutEnabled()
      ) {
        const prevItem = that.testController!.navigationController?.peekPreviousItem();
        if (prevItem!.section != that.currentItem!.section) {
          this.testController!.sessionController?.updatePauseState(PauseStateValue.SECTION_PAUSE).then(() => {
            if (that.testController!.getRedirectUrl != "" && 
            !that.testController!.navigationController?.endOfTest())
            {
              this.$cookies.remove('qti-player-token');
              this.testController!.reset();
              if (this.testController!.isTestNav) {
                window.location.href = this.testController!.baseServiceUrl + '/tester?from_app="testnav"';
              } else {
                window.location.href = that.testController!.baseServiceUrl + '/tester';
              }
            } else {
              that.handleExitTest();
            }
          });
        }
      }
    });
  }

  private handleEndOfTest(title: string, text: string): void {
    this.$cookies.remove('qti-player-token');

    if (this.testController && this.testController.sessionController) {
      this.testController.sessionController?.performFinalSubmit();
    } else {
      this.testController!.reset();
      this.handleRedirect(title, text, 0);
    }
  }

  private handleRedirect(title: string, text: string, delay: number): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    setTimeout(function () {
      AlertUtility.displayInformationDialog(title, text).then(() => {
        that.redirectToPlatform();
      });
    }, delay);
  }

  private handleCurrentItemNavigation(): void {
    this.currentItem = this.testController!.navigationController!.getCurrentItem();
  }

  private redirectToPlatform(): void {
    window.location.href = this.testController!.getEndTestRedirect();
  }
}
